import { fetchOperationLogs } from "@app/deploy";
import { useApi, useSelector } from "@app/react";
import { schema } from "@app/schema";
import type { DeployOperation } from "@app/types";
import { useEffect } from "react";
import { CopyTextButton } from "./copy";

export const LogLine = ({ text }: { text: string }) => {
  const parts = text.split("-- :");
  if (parts.length === 1) {
    return (
      <div>
        <span className="text-lime">{parts[0]}</span>
      </div>
    );
  }

  const leftPart = parts[0]
    .replace("+0000", "")
    .replace(/\d\d\d\d-\d\d-\d\d/, "")
    .trim();
  const rightPart = parts[1].trim();

  const Type = () => {
    if (leftPart.endsWith("ERROR")) {
      return <span className="text-red-300 break-all">{rightPart}</span>;
    }

    if (leftPart.endsWith("WARN")) {
      return <span className="text-orange-400 break-all">{rightPart}</span>;
    }

    return <span className="text-lime break-all">{rightPart}</span>;
  };

  return (
    <div className="text-sm">
      <span className="text-black-200">{leftPart}: </span>
      <Type />
    </div>
  );
};

const wrapper =
  "relative font-mono bg-black p-2 rounded-lg text-black-200 overflow-x";
export const LogViewerText = ({
  allowCopy,
  text,
}: { allowCopy?: boolean; text: string }) => {
  return (
    <div className={wrapper}>
      {allowCopy ? (
        <CopyTextButton
          placement="left"
          text={text}
          className="absolute right-2 top-4"
        />
      ) : null}
      {text.split("\n").map((line, i) => {
        return <LogLine key={`log-${i}`} text={line} />;
      })}
    </div>
  );
};

export const LogViewer = ({ op }: { op: DeployOperation }) => {
  const action = fetchOperationLogs({ id: op.id });
  const loader = useApi(action);
  const data: string = useSelector((s) =>
    schema.cache.selectById(s, { id: action.payload.key }),
  );
  useEffect(() => {
    if (op.status === "succeeded" || op.status === "failed") {
      loader.trigger();
    }
  }, [op.status]);

  if (op.status === "queued" || op.status === "running") {
    return (
      <div className={wrapper}>
        Operation {op.status}, logs will display after operation completes.
      </div>
    );
  }

  if (loader.isInitialLoading) {
    return <div className={wrapper}>Fetching logs...</div>;
  }

  if (!data || typeof data !== "string") {
    return <div className={wrapper}>No data found</div>;
  }

  return <LogViewerText allowCopy text={data} />;
};
